import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import ClearAll from "./ClearAll";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { useSelector } from "react-redux";
import {
  OwcIconButton,
  OwcTooltip,
  OwcTypography,
  OwcIcon,
  OwcListItem,
  OwcSelectDropdown,
  OwcEditableDropdown
} from "@one/react";
import { ALL_EQUIPMENT, LIST_VIEW, MY_LAB, SHOW_ADVANCE_FILTER_TOOLTIP } from "../../constants";
import Search from "./Search";
import moment from "moment-timezone";
import BookingViewChanger from "../booking-calendars/booking-view-changer/BookingViewChanger";
import { GoogleAuth } from "../../components/shared/GoogleAuth";
import { withApollo } from "react-apollo";
import { getAllData } from "../../utils/helpers/fetching";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../gql/bookingapi";
import { IconButton } from "@mui/material";
import ColumnsOutlinedGrey from "../../components/EquipmentList/icons/ColumnsOutlinedGrey";
import ColumnsOutlinedActive from "../../components/EquipmentList/icons/ColumnsOutlinedActive";
import FilterOutlinedGrey from "../../components/EquipmentList/icons/FilterOutlinedGrey";
import FilterOutlinedActive from "../../components/EquipmentList/icons/FilterOutlinedActive";
import { TOOL_PANELS } from "../../components/EquipmentList/Constants/constants";
import { getDefaultDateRange } from "../../utils/helpers/text";
import { handleOnCellClick } from "../booking-calendars/helpers";
import styles from "./BookingFilter.module.scss";

export const formatDateRange = (range) => {
  if (!range || range.length !== 2) return '';
  const [from, to] = range;
  return `${moment(from).format('DD MMM YYYY')} – ${moment(to).format('DD MMM YYYY')}`;
};

const BookingFilterStyled = styled.div`
  display: flex;
  align-items: start;
  padding-bottom: 10px;
  padding-left: 10px;
  flex-wrap: wrap;
  gap: 10px
`;


const BookingFilter = ({
  setResetAll,
  showAdvanceFilter,
  setShowAdvanceFilter,
  selectedDateRange,
  setSelectedDateRange,
  disableResetAll,
  setDisableResetAll,
  searchQuery,
  setSearchQuery,
  setTempRowData,
  view,
  setView,
  client,
  savedFilter,
  setSavedFilter,
  displayPreset,
  setDisplayPreset,
  setListGroupByView,
  listGroupByView,
  isEquipmentView,
  setIsEquipmentView,
  tempRowData,
  signeeFilter,
  setSigneeFilter,
  showFilters = true,
  setShowFilters,
  groupName,
  isGroupView = false,
  showInvalidFilter = false,
  userFilters,
  userDisplayColumnMaster,
  myLabFilter, currentSideBarPanel, setCurrentSideBarPanel, setShowSideBar, showSideBar,
  isFilterActive, setIsFilterActive, isColumnActive, setIsColumnActive
}) => {

  momentTimezone.moment = moment;
  const user = useSelector((state) => state.user);
  const { fetching, dispatchAction } = useContext(PaginationContext);
  const dataTimeZone = "utc";
  const displayTimeZone = user.timezone;
  const [allUsers, setAllUsers] = useState([]);

  const displayPresetOptions = useMemo(() => {
    return (userDisplayColumnMaster || [])
      .map((p) => ({ name: p.displayName + p.userID, label: p.displayName, definition: p.displayDefinition }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [userDisplayColumnMaster]);

  const handleDisplayPresetChange = useCallback((selected) => {
    setDisplayPreset(selected); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedFilterOptions = useMemo(() => {
    const myLab = [];
    const allEquipment = [{ name: ALL_EQUIPMENT, label: "All Equipment" }];
    const rest = [];

    if (myLabFilter) {
      myLab.push({ name: MY_LAB, label: "My Lab" });
    }

    userFilters.forEach(filter => {
      if (filter.filterName && filter.filterName !== MY_LAB) {
        rest.push({ name: filter.filterName, label: filter.filterName });
      }
    });

    rest.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    return [...myLab, ...allEquipment, ...rest];
  }, [myLabFilter, userFilters]);

  const loadUsers = useCallback(async () => {
    try {
      const { items } = await getAllData({
        client,
        query: LIST_DIGITAL_LAB_BOOKING_USERS,
        dataPath: ["data", "listDigitalLabBookingUsers"],
        variables: { limit: 1000 },
        cache: true,
      });

      setAllUsers(items || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [client]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if ((savedFilter && savedFilter !== '__ALL_EQUIPMENT__') || displayPreset || searchQuery !== '') {
      setDisableResetAll(false);
    }
  }, [savedFilter, setDisableResetAll, displayPreset, searchQuery]);

  useEffect(() => {
    if (!savedFilter && savedFilterOptions.length > 0) {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption) {
        setSavedFilter(MY_LAB);
      } else {
        setSavedFilter(savedFilterOptions[0].name);
      }
    }
  }, [myLabFilter, userFilters, allUsers, savedFilter, savedFilterOptions, setSavedFilter, setSigneeFilter]);

  const handleSavedFilterChange = useCallback((selectedFilterName) => {
    if (selectedFilterName === "" && (!signeeFilter || signeeFilter === "" || signeeFilter === "All users")) {
      return;
    }
    if (!isGroupView) {
      setSavedFilter(selectedFilterName);
    }

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signeeFilter, user.user, allUsers, setSavedFilter, dispatchAction, setTempRowData, setDisableResetAll, isGroupView]);

  const setDefaultTimeRange = useCallback(() => {
    setSelectedDateRange(getDefaultDateRange());
  }, [setSelectedDateRange]);

  const handleReset = useCallback(() => {
    let defaultSavedFilter = "";
    if (savedFilterOptions.length > 0) {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption) {
        defaultSavedFilter = MY_LAB;
      } else {
        defaultSavedFilter = '__ALL_EQUIPMENT__';
      }
      setSavedFilter(defaultSavedFilter);
    }

    setTimeout(() => setDefaultTimeRange())
    setSearchQuery("");
    setResetAll(prev => !prev);
    setTempRowData([]);
    handleDisplayPresetChange(null)
    setDisableResetAll(true);

    dispatchAction({ type: "clearAll" }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterOptions, setListGroupByView, displayPreset, allUsers, setSavedFilter, setSigneeFilter, setSearchQuery, setResetAll, setTempRowData, setDisableResetAll, setDisplayPreset, dispatchAction]);

  // Add effect to handle view switching
  useEffect(() => {
    // Clear temporary data when switching views to prevent stale data

    setTempRowData([]);
    dispatchAction({ type: "clearAll" });
  }, [view, isEquipmentView, setTempRowData, dispatchAction]);


  // Add effect to handle groupName changes in group view
  useEffect(() => {
    if (isGroupView && groupName && groupName !== savedFilter && savedFilterOptions.find((option) => option.name === groupName)) {
      setSavedFilter(groupName);
      // Clear temporary data when group changes
      setTempRowData([]);
      dispatchAction({ type: "clearAll" });
      setDisableResetAll(false);
    }
  }, [groupName, isGroupView, savedFilter, setSavedFilter, setTempRowData, dispatchAction, setDisableResetAll, savedFilterOptions]);

  const isResetDisabled = useMemo(() => {
    let defaultSavedFilter = "";
    const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
    if (myLabOption) {
      defaultSavedFilter = MY_LAB;
    } else {
      defaultSavedFilter = '__ALL_EQUIPMENT__';
    }

    return savedFilter === defaultSavedFilter &&
      searchQuery === "" && !displayPreset;
  }, [savedFilterOptions, savedFilter, searchQuery, displayPreset]);

  return (
    <>
      <div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: '10px' }}>
        <OwcTypography className={styles.pageTitle} variant="button">{`My Bookings`}</OwcTypography>
        <div style={{ marginLeft: "auto 0" }}>
          <GoogleAuth />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >

        <>
          <BookingFilterStyled data-testid="booking-filter-admin">
            <div style={{ display: view === LIST_VIEW ? 'block' : 'none' }}>
              <Search
                onChange={(e) => setSearchQuery(e.detail)}
                onClear={() => setSearchQuery("")}
                label="Search all columns"
                placeholder="Search all columns"
                testid={"common-search-drop-booking"}
                fetching={fetching}
                compact={false}
                searchQuery={searchQuery}
              ></Search>
            </div>

            {view === LIST_VIEW && (<div style={{ padding: "0 5px 0 5px" }} className="date-picker-my-booking">
              <Datepicker
                id="datepicker-admin-panel"
                controls={["calendar"]}
                value={selectedDateRange}
                inputStyle="box"
                style={{ height: "5px" }}
                select="range"
                display="anchored"
                inRangeInvalid={true}
                rangeEndInvalid={true}
                startIcon={<OwcIcon name="date" type="outlined" />}
                onTempChange={handleOnCellClick}
                headerText="Experiment date(range)"
                label={"Date range"}
                touchUi={"auto"}
                dateFormat="D-MMM-YYYY"
                onClose={(param) => {
                  if (param?.value) {
                    dispatchAction({
                      type: "clearAll",
                    });
                    setTempRowData([]);
                    setDisableResetAll(false);
                    setSelectedDateRange(param?.value);
                  }
                }}
                timezonePlugin={momentTimezone}
                dataTimezone={dataTimeZone}
                displayTimezone={displayTimeZone}
                buttons={["cancel", "set"]}
                compact
                exclusiveEndDates={false}
              />
            </div>)}

            <div style={{ display: (view === LIST_VIEW && listGroupByView === 'BOOKING') ? 'block' : 'none', margin: 'auto 0' }}>
              <ClearAll
                onClickCallback={handleReset}
                disabled={isResetDisabled}
              />
            </div>

            <div style={{ display: (view === LIST_VIEW && listGroupByView !== "BOOKING") ? 'block' : 'none', margin: 'auto 0' }}>
              <OwcTooltip anchor={"advance-filter"} placement="top">
                {SHOW_ADVANCE_FILTER_TOOLTIP}
              </OwcTooltip>
              <OwcIconButton
                id="advance-filter"
                data-testid="advance-filter-button"
                style={{ marginRight: 8 }}
                icon=" parameter_set"
                type="filled"
                onClick={() => {
                  setShowAdvanceFilter((prev) => !prev);
                }}
                flat
              />
            </div>
            {showAdvanceFilter && (
              <>
                {/* ➊ PREDEFINED FILTER */}
                <div style={{ display: view === LIST_VIEW ? 'block' : 'none' }}>
                  <div style={{ display: 'flex' }}>

                    {!(listGroupByView === 'BOOKING' && view === LIST_VIEW) && (<OwcSelectDropdown
                      label="Select predefined filter"
                      disabled={listGroupByView === 'BOOKING' && view === LIST_VIEW}
                      style={{ width: 200, marginLeft: 8, marginRight: 8 }}
                      onSelectChange={(e) => {
                        const opt = savedFilterOptions[e.detail[0]];
                        if (opt) {
                          handleSavedFilterChange(opt.name);
                        }
                      }}
                    >
                      {(savedFilterOptions || []).map((o) => (
                        <OwcListItem key={o.name} value={o.name} selected={savedFilter === o.name}>
                          {o.label}
                        </OwcListItem>
                      ))}
                    </OwcSelectDropdown>)}


                    {listGroupByView !== 'BOOKING' && view === LIST_VIEW && (<IconButton
                      id="filter-list"
                      onClick={() => {
                        setIsColumnActive(() => false);
                        if (showSideBar) {
                          setShowSideBar(false);
                          setCurrentSideBarPanel(TOOL_PANELS.filters);
                          if (!isFilterActive) {
                            setTimeout(() => {
                              setShowSideBar(true);
                            });
                          }
                          setIsFilterActive((prevState) => !prevState);
                        } else {
                          setCurrentSideBarPanel(TOOL_PANELS.filters);
                          setIsFilterActive(() => true);
                          setShowSideBar(() => true);
                        }
                      }}
                    >
                      {!isFilterActive && (
                        <>
                          <FilterOutlinedGrey />
                        </>
                      )}
                      {isFilterActive && (
                        <>
                          <FilterOutlinedActive />
                        </>
                      )}
                    </IconButton>)}
                  </div>
                </div>

                {/* ➋ PREDEFINED COLUMNS */}
                {(view === LIST_VIEW && !(listGroupByView === 'BOOKING' && view === LIST_VIEW)) && (
                  <div style={{ display: 'flex' }}>
                    <OwcEditableDropdown
                      key={displayPreset?.name}
                      label="Predefined columns"
                      disabled={listGroupByView === 'BOOKING' && view === LIST_VIEW}
                      style={{ width: 200, marginRight: 8 }}
                      onSelectChange={(ev) => {
                        const selected = displayPresetOptions[ev.detail];
                        if (selected) {
                          handleDisplayPresetChange(selected);
                        } else handleDisplayPresetChange(null);
                      }}
                    >
                      {displayPresetOptions.map((o) => (
                        <OwcListItem key={o.name} value={o.name} selected={displayPreset?.name === o.name}>
                          {o.label}
                        </OwcListItem>
                      ))}
                    </OwcEditableDropdown>
                    {listGroupByView !== 'BOOKING' && view === LIST_VIEW && (<IconButton
                      id="column-filter"
                      onClick={() => {
                        setIsFilterActive(() => false);
                        if (showSideBar) {
                          setShowSideBar(false);
                          setCurrentSideBarPanel(TOOL_PANELS.columns);
                          if (!isColumnActive) {
                            setTimeout(() => {
                              setShowSideBar(true);
                            });
                          }
                          setIsColumnActive((prevState) => !prevState);
                        } else {
                          setCurrentSideBarPanel(TOOL_PANELS.columns);
                          setIsColumnActive(() => true);
                          setShowSideBar(() => true);
                        }
                      }}
                    >
                      {!isColumnActive && (
                        <>
                          <ColumnsOutlinedGrey />
                        </>
                      )}
                      {isColumnActive && (
                        <>
                          <ColumnsOutlinedActive />
                        </>
                      )}
                    </IconButton>)}
                  </div>
                )}

                <div style={{ display: (view === LIST_VIEW && listGroupByView !== 'BOOKING') ? 'block' : 'none', margin: 'auto 0' }}>
                  <ClearAll
                    onClickCallback={handleReset}
                    disabled={isResetDisabled}
                  />
                </div>

              </>
            )}
          </BookingFilterStyled>

          <BookingViewChanger setTempRowData={setTempRowData} myBookingsView view={view} setView={setView} setListGroupByView={setListGroupByView} listGroupByView={listGroupByView} isEquipmentView={isEquipmentView} setIsEquipmentView={setIsEquipmentView} />
        </>


      </div>
    </>
  );
};

export default withApollo(BookingFilter);
